import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import {
  FaCalendarAlt,
  FaCommentMedical,
  FaFileAlt,
  FaHeadset,
  FaPaperPlane,
  FaPills,
  FaPlus,
  FaRobot,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import useProfileStore from "../../store/patient/profileStore";
import useAIChat from "./useAIChat";
import useAgentBridge from "./useAgentBridge";
import { formatTime, renderMarkdownLite } from "./chatFormatting";

/* ===================== theme ===================== */

const primaryColor = "#0cb785";
const gradientBg = "linear-gradient(135deg, #0cb785 0%, #0a9c72 100%)";
const agentColor = "#6366f1";
const textColor = "#1e293b";
const mutedColor = "#64748b";
const borderColor = "#e2e8f0";

/* ===================== animations ===================== */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const panelIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(12, 183, 133, 0.35); }
  70% { box-shadow: 0 0 0 12px rgba(12, 183, 133, 0); }
`;

const blink = keyframes`
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
`;

/* ===================== layout ===================== */

const FloatingButton = styled.button`
  position: fixed;
  bottom: 1.75rem;
  right: 1.75rem;
  width: 62px;
  height: 62px;
  border-radius: 18px;
  background: ${gradientBg};
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(12, 183, 133, 0.35);
  z-index: 1050;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${pulse} 3s infinite;

  &:hover {
    transform: translateY(-3px) scale(1.04);
  }

  svg {
    width: 26px;
    height: 26px;
  }

  @media (max-width: 576px) {
    bottom: 1.25rem;
    right: 1.25rem;
    width: 56px;
    height: 56px;
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  border-radius: 11px;
  background: #ef4444;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
`;

const Panel = styled.div`
  position: fixed;
  bottom: 6.25rem;
  right: 1.75rem;
  width: 400px;
  max-width: calc(100vw - 2rem);
  height: min(640px, calc(100vh - 8rem));
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.28);
  z-index: 1049;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${panelIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 576px) {
    right: 0;
    bottom: 0;
    width: 100vw;
    max-width: 100vw;
    height: 100dvh;
    border-radius: 0;
  }
`;

const Header = styled.div`
  padding: 1rem 1.25rem;
  background: ${(props) => (props.$agentMode ? `linear-gradient(135deg, ${agentColor} 0%, #4f46e5 100%)` : gradientBg)};
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const HeaderIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
  }

  span {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    opacity: 0.9;
  }
`;

const StatusDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$online ? "#4ade80" : "#fbbf24")};
  display: inline-block;
  flex-shrink: 0;
`;

const HeaderButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.18);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.1rem;
  background: #f8fafc;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const MessageRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  animation: ${fadeIn} 0.25s ease-out;

  ${(props) =>
    props.$isUser &&
    css`
      flex-direction: row-reverse;
    `}
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(props) => {
    if (props.$sender === "user") {
      return css`
        background: ${primaryColor};
        color: #fff;
      `;
    }
    if (props.$sender === "agent") {
      return css`
        background: ${agentColor};
        color: #fff;
      `;
    }
    return css`
      background: #fff;
      color: ${primaryColor};
      border: 2px solid ${primaryColor};
    `;
  }}

  svg {
    width: 13px;
    height: 13px;
  }
`;

const Bubble = styled.div`
  max-width: 82%;
  border-radius: 16px;
  padding: 0.7rem 0.9rem;
  font-size: 0.875rem;
  line-height: 1.55;

  ${(props) => {
    if (props.$sender === "user") {
      return css`
        background: ${primaryColor};
        color: #fff;
        border-bottom-right-radius: 4px;
      `;
    }
    if (props.$sender === "agent") {
      return css`
        background: #eef2ff;
        color: ${textColor};
        border: 1px solid #c7d2fe;
        border-bottom-left-radius: 4px;
      `;
    }
    return css`
      background: #fff;
      color: ${textColor};
      border: 1px solid ${(p) => (p.$error ? "#fecaca" : borderColor)};
      border-bottom-left-radius: 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    `;
  }}

  ${(props) =>
    props.$error &&
    css`
      background: #fef2f2;
      border-color: #fecaca;
    `}

  p {
    margin: 0 0 0.4rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 0.1em 0.35em;
    font-size: 0.82em;
  }

  /* Auto-linked emails / URLs — make them clearly clickable. */
  a:not(.chat-link-btn) {
    color: ${(props) => (props.$sender === "user" ? "#eafff5" : primaryColor)};
    font-weight: 600;
    text-decoration: underline;
    word-break: break-word;

    &:hover {
      opacity: 0.85;
    }
  }

  a.chat-link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.5rem;
    padding: 0.55rem 1.1rem;
    background: ${gradientBg};
    color: #fff;
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: 10px;
    text-decoration: none;
    box-shadow: 0 3px 10px rgba(12, 183, 133, 0.28);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(12, 183, 133, 0.38);
    }
  }
`;

const SenderLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${agentColor};
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Timestamp = styled.div`
  font-size: 0.68rem;
  opacity: 0.65;
  margin-top: 5px;
`;

const TypingDots = styled.span`
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${mutedColor};
    animation: ${blink} 1.2s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

const AgentBanner = styled.div`
  padding: 0.55rem 1.1rem;
  background: #eef2ff;
  border-bottom: 1px solid #c7d2fe;
  color: #3730a3;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  svg {
    flex-shrink: 0;
  }
`;

const BannerText = styled.span`
  flex: 1;
  min-width: 0;
`;

const BackToAnnaBar = styled.div`
  padding: 0.6rem 1rem 0;
  background: #fff;
`;

const BackToAnnaButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.6rem 1rem;
  border-radius: 12px;
  border: none;
  background: ${gradientBg};
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(12, 183, 133, 0.3);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(12, 183, 133, 0.42);
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const Suggestions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const SuggestionChip = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.7rem;
  border-radius: 12px;
  border: 1.5px solid ${borderColor};
  background: #fff;
  text-align: left;
  font-size: 0.78rem;
  color: ${textColor};
  cursor: pointer;
  transition: all 0.18s;

  svg {
    color: ${primaryColor};
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }

  &:hover {
    border-color: ${primaryColor};
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  }
`;

const InputBar = styled.div`
  padding: 0.9rem 1rem;
  border-top: 1px solid ${borderColor};
  background: #fff;
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
`;

const TextInput = styled.textarea`
  flex: 1;
  resize: none;
  max-height: 110px;
  min-height: 44px;
  padding: 0.65rem 0.9rem;
  border: 2px solid ${borderColor};
  border-radius: 12px;
  font-size: 0.875rem;
  color: ${textColor};
  font-family: inherit;
  line-height: 1.4;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${primaryColor};
  }

  &:disabled {
    background: #f8fafc;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${gradientBg};
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  align-self: flex-end;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(12, 183, 133, 0.3);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/* ===================== helpers ===================== */

const SUGGESTIONS = [
  { icon: FaCalendarAlt, text: "Book an appointment" },
  { icon: FaFileAlt, text: "What do my latest vitals look like?" },
  { icon: FaPills, text: "Order medication from the pharmacy" },
];

/* ===================== component ===================== */

export default function AIChatWidget() {
  const { profile } = useProfileStore();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const patientId = useMemo(() => {
    if (profile && !Array.isArray(profile) && profile.id) {
      return profile.id;
    }
    return window.localStorage.getItem("userId") || null;
  }, [profile]);

  const {
    messages,
    sending,
    connected,
    agentSession,
    unreadCount,
    sendMessage,
    startNewConversation,
    clearUnread,
  } = useAIChat({ patientId, isOpen });

  const liveAgent = useAgentBridge({ patientId, patient: profile });

  // Live agent (real EMR support agent over the media server) takes over the panel.
  const liveMode = liveAgent.status !== "idle";
  const displayMessages = liveMode ? liveAgent.messages : messages;

  const agentMode =
    liveMode ||
    Boolean(agentSession && (agentSession.status === "active" || agentSession.queued));

  useEffect(() => {
    if (isOpen) {
      clearUnread();
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages, liveAgent.messages, clearUnread]);

  // The support page hosts the full-page chat; don't stack the widget on top of it.
  if (!patientId || pathname.startsWith("/dashboard/support")) {
    return null;
  }

  const handleSend = () => {
    const text = input;
    if (!text.trim()) return;
    setInput("");
    if (liveMode) {
      liveAgent.sendMessage(text);
    } else {
      sendMessage(text);
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const showSuggestions = !liveMode && messages.length <= 1 && !sending;

  const liveStatusLabel =
    liveAgent.status === "connected"
      ? `Connected to ${liveAgent.agentName || "a support agent"}`
      : liveAgent.status === "waiting"
        ? "Waiting for an agent to pick up…"
        : "Reconnecting…";

  const statusLabel = liveMode
    ? liveStatusLabel
    : agentMode
      ? agentSession.status === "active"
        ? `Connected to ${agentSession.agentName || "a support agent"}`
        : `In queue${agentSession.queuePosition ? ` — position ${agentSession.queuePosition}` : ""}`
      : connected
        ? "Online"
        : "Connecting…";

  return (
    <>
      <FloatingButton
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close health assistant" : "Open health assistant"}
      >
        {isOpen ? <FaTimes /> : <FaCommentMedical />}
        {!isOpen && unreadCount > 0 && (
          <UnreadBadge>{unreadCount > 9 ? "9+" : unreadCount}</UnreadBadge>
        )}
      </FloatingButton>

      {isOpen && (
        <Panel role="dialog" aria-label="Health assistant chat">
          <Header $agentMode={agentMode}>
            <HeaderIcon>{agentMode ? <FaHeadset /> : <FaRobot />}</HeaderIcon>
            <HeaderText>
              <h4>{liveMode ? "Support Team" : agentMode ? "Support Team" : "Anna — Health Assistant"}</h4>
              <span>
                <StatusDot $online={liveMode ? liveAgent.status === "connected" : connected} />
                {statusLabel}
              </span>
            </HeaderText>
            {liveMode ? (
              <HeaderButton onClick={liveAgent.endSession} title="Back to Anna (AI)" aria-label="Back to AI assistant">
                <FaRobot />
              </HeaderButton>
            ) : (
              <HeaderButton
                onClick={() => liveAgent.startSession()}
                title="Talk to a human agent"
                aria-label="Talk to a human agent"
              >
                <FaHeadset />
              </HeaderButton>
            )}
            {!liveMode && (
              <HeaderButton onClick={startNewConversation} title="Start a new conversation" aria-label="Start a new conversation">
                <FaPlus />
              </HeaderButton>
            )}
            <HeaderButton onClick={() => setIsOpen(false)} title="Close" aria-label="Close chat">
              <FaTimes />
            </HeaderButton>
          </Header>

          {liveMode ? (
            <AgentBanner>
              <FaHeadset size={13} />
              <BannerText>
                {liveAgent.status === "connected"
                  ? `You're chatting with ${liveAgent.agentName || "our support team"}. Messages go directly to your agent.`
                  : "Connecting you with our support team — you'll be able to chat as soon as an agent picks up."}
              </BannerText>
            </AgentBanner>
          ) : (
            agentMode && (
              <AgentBanner>
                <FaHeadset size={13} />
                <BannerText>
                  {agentSession.status === "active"
                    ? "You're chatting with our support team. Messages go directly to your agent."
                    : "All agents are busy right now — you're in the queue and will be connected shortly."}
                </BannerText>
              </AgentBanner>
            )
          )}

          <MessagesArea>
            {displayMessages.map((message) => (
              <MessageRow
                key={message.id}
                $isUser={message.sender === "user"}
                style={message.sender === "system" ? { justifyContent: "center" } : undefined}
              >
                {message.sender === "system" ? (
                  <Bubble $sender="bot" style={{ maxWidth: "90%", textAlign: "center", fontStyle: "italic", opacity: 0.8 }}>
                    <span style={{ whiteSpace: "pre-wrap" }}>{message.text}</span>
                  </Bubble>
                ) : (
                  <>
                    <Avatar $sender={message.sender}>
                      {message.sender === "user" ? (
                        <FaUser />
                      ) : message.sender === "agent" ? (
                        <FaHeadset />
                      ) : (
                        <FaRobot />
                      )}
                    </Avatar>
                    <Bubble $sender={message.sender} $error={message.error}>
                      {message.sender === "agent" && (
                        <SenderLabel>{message.agentName || "Support Agent"}</SenderLabel>
                      )}
                      {message.streaming && !message.text ? (
                        <TypingDots>
                          <i />
                          <i />
                          <i />
                        </TypingDots>
                      ) : message.sender === "user" ? (
                        <span style={{ whiteSpace: "pre-wrap" }}>{message.text}</span>
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{ __html: renderMarkdownLite(message.text) }}
                        />
                      )}
                      <Timestamp>{formatTime(message.timestamp)}</Timestamp>
                    </Bubble>
                  </>
                )}
              </MessageRow>
            ))}

            {!liveMode && sending && !messages.some((message) => message.streaming) && (
              <MessageRow>
                <Avatar $sender="bot">
                  <FaRobot />
                </Avatar>
                <Bubble $sender="bot">
                  <TypingDots>
                    <i />
                    <i />
                    <i />
                  </TypingDots>
                </Bubble>
              </MessageRow>
            )}

            {showSuggestions && (
              <>
                <Suggestions>
                  {SUGGESTIONS.map((suggestion) => (
                    <SuggestionChip
                      key={suggestion.text}
                      onClick={() => sendMessage(suggestion.text)}
                    >
                      <suggestion.icon />
                      {suggestion.text}
                    </SuggestionChip>
                  ))}
                </Suggestions>
                <SuggestionChip
                  onClick={() => liveAgent.startSession()}
                  style={{ marginTop: "0.55rem", width: "100%", justifyContent: "center" }}
                >
                  <FaHeadset />
                  Talk to a human support agent
                </SuggestionChip>
              </>
            )}

            <div ref={messagesEndRef} />
          </MessagesArea>

          {(liveMode || agentMode) && (
            <BackToAnnaBar>
              <BackToAnnaButton onClick={liveAgent.endSession} title="Return to the AI assistant">
                <FaRobot /> Back to Anna (AI Assistant)
              </BackToAnnaButton>
            </BackToAnnaBar>
          )}

          <InputBar>
            <TextInput
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={liveMode || agentMode ? "Message the support team…" : "Ask about your health, appointments…"}
              disabled={!liveMode && sending}
              aria-label="Type your message"
            />
            <SendButton
              onClick={handleSend}
              disabled={!input.trim() || (!liveMode && sending)}
              aria-label="Send message"
            >
              <FaPaperPlane size={16} />
            </SendButton>
          </InputBar>
        </Panel>
      )}
    </>
  );
}
